/**
 * Page Context Detection Utility
 *
 * Detects what page the user is on (product, collection, cart, etc.)
 * to provide contextually relevant AI assistance.
 *
 * Detection priority:
 * 1. Explicit config (window.CloseraConfig.pageContext)
 * 2. Shopify globals (window.Shopify)
 * 3. URL patterns (fallback)
 */

export interface PageContext {
  url: string;
  pageType: 'product' | 'collection' | 'cart' | 'search' | 'home' | 'other';
  productSku?: string;
  collectionHandle?: string;
  searchQuery?: string;
}

// Window.Shopify and Window.CloseraConfig types are in src/types/global.d.ts

/**
 * Detect page context from Shopify globals.
 * Available on Shopify storefronts.
 */
function detectFromShopify(): PageContext {
  const shopify = window.Shopify;
  const rawPageType = shopify?.pageType || 'other';

  // Map Shopify page types to our types
  let pageType: PageContext['pageType'];
  switch (rawPageType) {
    case 'product':
      pageType = 'product';
      break;
    case 'collection':
      pageType = 'collection';
      break;
    case 'cart':
      pageType = 'cart';
      break;
    case 'search':
      pageType = 'search';
      break;
    case 'index':
      pageType = 'home';
      break;
    default:
      pageType = 'other';
  }

  const context: PageContext = {
    url: window.location.href,
    pageType,
  };

  // Extract product handle from URL (always reliable — shopify.product.handle
  // can be undefined/stale on some stores like Freakins)
  if (pageType === 'product') {
    const pathname = window.location.pathname;
    if (pathname.includes('/products/')) {
      const handle = pathname.split('/products/')[1]?.split('?')[0]?.split('/')[0];
      if (handle) context.productSku = handle;
    }
  }

  // Extract collection handle if on collection page
  if (pageType === 'collection' && shopify?.collection?.handle) {
    context.collectionHandle = shopify.collection.handle;
  }

  // Extract search query from URL if on search page
  if (pageType === 'search') {
    const params = new URLSearchParams(window.location.search);
    context.searchQuery = params.get('q') || undefined;
  }

  return context;
}

/**
 * Detect page context from URL patterns.
 * Fallback when Shopify globals aren't available.
 */
function detectFromUrl(): PageContext {
  const pathname = window.location.pathname;

  // Product page: /products/[handle]
  if (pathname.includes('/products/')) {
    const handle = pathname.split('/products/')[1]?.split('?')[0]?.split('/')[0];
    return {
      url: window.location.href,
      pageType: 'product',
      productSku: handle || undefined,
    };
  }

  // Collection page: /collections/[handle]
  if (pathname.includes('/collections/')) {
    const handle = pathname.split('/collections/')[1]?.split('/')[0]?.split('?')[0];
    return {
      url: window.location.href,
      pageType: 'collection',
      collectionHandle: handle || undefined,
    };
  }

  // Cart page
  if (pathname.includes('/cart')) {
    return { url: window.location.href, pageType: 'cart' };
  }

  // Search page
  if (pathname.includes('/search')) {
    const params = new URLSearchParams(window.location.search);
    return {
      url: window.location.href,
      pageType: 'search',
      searchQuery: params.get('q') || undefined,
    };
  }

  // Home page
  if (pathname === '/' || pathname === '') {
    return { url: window.location.href, pageType: 'home' };
  }

  return { url: window.location.href, pageType: 'other' };
}

/**
 * Detect the current page context.
 *
 * Detection priority:
 * 1. Explicit config (window.CloseraConfig.pageContext) - highest priority
 * 2. Shopify globals (window.Shopify.pageType) - if pageType is available
 * 3. URL patterns - fallback (always works)
 */
export function detectPageContext(): PageContext {
  // 1. Check explicit config (highest priority)
  const config = window.CloseraConfig?.pageContext;
  if (config) {
    return {
      url: window.location.href,
      pageType: config.pageType || 'other',
      ...config,
    };
  }

  // 2. Check Shopify globals - only if pageType is actually set
  // Some Shopify stores have window.Shopify but don't populate pageType
  if (window.Shopify?.pageType) {
    return detectFromShopify();
  }

  // 3. Fallback to URL patterns (most reliable)
  return detectFromUrl();
}
